import { useState, type FormEvent } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ErrorBanner, Field } from '../components/ui'

export function LoginPage() {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(undefined)

    try {
      await signIn(email, password)
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={(e) => void submit(e)}>
        <h1>ALX Accounting</h1>
        <p className="subtitle">Sign in to continue</p>

        <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </Field>

        <Field label="Password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <button className="primary" style={{ width: '100%', marginTop: 6 }} disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}

/**
 * Shown when the account still carries the bootstrap password an administrator set. Blocks the
 * rest of the application until it is changed — a credential someone else knows is not a login.
 */
export function ChangePasswordPage() {
  const { changePassword, signOut } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const [done, setDone] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()

    if (next !== confirm) {
      setError(new Error('The two new passwords do not match.'))
      return
    }

    setBusy(true)
    setError(undefined)

    try {
      await changePassword(current, next)
      setDone(true)
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Password changed</h1>
          <p className="subtitle">Every session was signed out. Sign in again with the new password.</p>
          <button className="primary" style={{ width: '100%' }} onClick={() => void signOut()}>
            Go to sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={(e) => void submit(e)}>
        <h1>Change your password</h1>
        <p className="subtitle">
          This account is still using the password it was created with. Choose your own before
          continuing.
        </p>

        <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

        <Field label="Current password">
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <Field label="New password" hint="At least 10 characters, with a digit and a lower-case letter.">
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </Field>

        <Field label="Confirm new password">
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <button className="primary" style={{ width: '100%', marginTop: 6 }} disabled={busy}>
          {busy ? 'Saving…' : 'Change password'}
        </button>
      </form>
    </div>
  )
}
